import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HmiCrop } from './HmiView';
import { STATE_IDS, TARGET, pad } from './screenData';
import { TASK_IDS, buildTask } from './tasks';
import { EASE, Intro, Reveal, Segmented } from './ui';
import { StudyText } from './studies';
import styles from './ScreenInspector.module.css';

const swap = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.3, ease: EASE },
};

export default function QuestionTypes({ t, lang }) {
    const q = t.types;
    const [taskId, setTaskId] = useState('check');
    const [state, setState] = useState('ready');
    const task = useMemo(() => buildTask(taskId, lang, state), [taskId, lang, state]);
    const target = TARGET[task.target];
    const answer = task.pixels ? task.answer : task.reply;

    return (
        <>
            <Intro eyebrow={q.eyebrow} title={q.title} sub={q.sub} />

            <Reveal className={styles.centerRow}>
                <Segmented
                    group="types"
                    label={q.eyebrow}
                    options={TASK_IDS.map((id) => ({ id, label: q.names[id] }))}
                    value={taskId}
                    onChange={setTaskId}
                />
            </Reveal>

            <Reveal className={styles.typeCard} delay={0.1}>
                <div className={styles.typeImage}>
                    <AnimatePresence mode="wait">
                        <motion.div key={`${task.target}-${state}`} className={styles.typeImageInner} {...swap}>
                            <HmiCrop state={state} box={pad(target.box, 6)} label={task.target} />
                        </motion.div>
                    </AnimatePresence>
                    <code className={styles.typeTarget}>{task.target}</code>
                </div>

                <div className={styles.typeText}>
                    <p className={styles.label}>{q.question}</p>
                    <AnimatePresence mode="wait">
                        <motion.p key={task.question} className={styles.question} {...swap}>{task.question}</motion.p>
                    </AnimatePresence>

                    <p className={styles.label}>{q.answer}</p>
                    <AnimatePresence mode="wait">
                        <motion.p key={`${taskId}-${answer}`} className={styles.answer} {...swap}>{answer}</motion.p>
                    </AnimatePresence>

                    {task.pixels && (
                        <div className={styles.refs}>
                            {task.refs.map((r) => (
                                <div key={r.name} className={`${styles.ref} ${r.name === task.answer ? styles.refWin : ''}`}>
                                    <div className={styles.refImg}><HmiCrop state={r.state} box={target.box} label={r.name} /></div>
                                    <span>{r.name} · {r.distance.toFixed(1)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <p className={styles.answerNote}><StudyText text={task.pixels ? q.pixels : q.model} /></p>
                    <code className={styles.assertCode}>{task.assertLine}</code>
                </div>
            </Reveal>

            <div className={styles.centerRow}>
                <span className={styles.controlLabel}>{t.stateLabel}</span>
                <Segmented
                    small
                    group="state-types"
                    label={t.stateLabel}
                    options={STATE_IDS.map((id) => ({ id, label: t.states[id] }))}
                    value={state}
                    onChange={setState}
                />
            </div>
            <p className={styles.note}><StudyText text={q.rules} /></p>
        </>
    );
}
